import { createContext, useContext, useEffect, useSyncExternalStore } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { setSiteHtmlLang } from '@/lib/i18n';
import { ensureSiteLanguage, isSiteLanguageLoaded, siteInstance, subscribeSiteLanguages } from '@/lib/siteI18n';
import { DEFAULT_LANGUAGE, splitLocalePath } from '@/lib/siteLanguages';

// The language of the public page being shown. null outside the public
// website (the app's screens), which keep the app's own language.
const SiteLangContext = createContext(null);

export const useSiteLang = () => useContext(SiteLangContext) || DEFAULT_LANGUAGE;

// A public page in `lang`: its texts, the words of the app it borrows (the
// landing's) and <html lang> all follow the page, not the app's language.
export function SiteLang({ lang, children }) {
  const loaded = useSyncExternalStore(
    subscribeSiteLanguages,
    () => isSiteLanguageLoaded(lang),
    () => isSiteLanguageLoaded(lang),
  );
  // index.js and the language menu load the language before showing the page;
  // this only covers a link followed before its file arrived.
  useEffect(() => { if (!loaded) ensureSiteLanguage(lang).catch(() => {}); }, [lang, loaded]);
  const shown = loaded ? lang : DEFAULT_LANGUAGE;
  useEffect(() => {
    setSiteHtmlLang(shown);
    return () => setSiteHtmlLang(null);
  }, [shown]);
  return (
    <SiteLangContext.Provider value={shown}>
      <I18nextProvider i18n={siteInstance(shown)}>{children}</I18nextProvider>
    </SiteLangContext.Provider>
  );
}

// Layout route of every public page (App.js): the language comes from the
// address — /fr/… is French, an address without prefix is Spanish.
export function SitePages() {
  const { pathname } = useLocation();
  return (
    <SiteLang lang={splitLocalePath(pathname).lang || DEFAULT_LANGUAGE}>
      <Outlet />
    </SiteLang>
  );
}
